import { useCallback, useEffect, useRef } from 'react'
import { useStorePads } from '../store'
import { createPlayer, setHighpassFrequency, setLowpassFrequency, setMasterVolume } from '../audioManager'

const useSoundPlayer = () => {
    const pads = useStorePads((state) => state.pads)
    const libraries = useStorePads((state) => state.libraries)
    const currentLibraryName = useStorePads((state) => state.currentLibrary.name)
    const playback = useStorePads((state) => state.playback)
    const volume = useStorePads((state) => state.volume)
    const highpassFrequency = useStorePads((state) => state.highpassFrequency)
    const lowpassFrequency = useStorePads((state) => state.lowpassFrequency)
    const setSoundsLoaded = useStorePads((state) => state.setSoundsLoaded)
    const previousPlaybackRef = useRef(null)
    const loadingLibrariesRef = useRef(new Map())

    const isLibraryReady = useCallback((library) => {
        if (!library?.name) return false

        const libraryPads = useStorePads.getState().pads[library.name]
        if (!Array.isArray(libraryPads)) return false

        return libraryPads.length === library.sources.length
    }, [])

    const getPadPlayer = useCallback((playbackState) => {
        if (!playbackState?.library || !playbackState?.note) return null

        const libraryPads = pads[playbackState.library] || []
        const matchedPad = libraryPads.find((pad) => pad.note === playbackState.note)
        return matchedPad?.player || null
    }, [pads])

    const loadAudioLibrary = useCallback((library) => {
        if (!library?.name) return Promise.resolve([])

        const existingPads = useStorePads.getState().pads[library.name]
        if (Array.isArray(existingPads) && existingPads.length === library.sources.length) {
            return Promise.resolve(existingPads)
        }

        const pendingLoad = loadingLibrariesRef.current.get(library.name)
        if (pendingLoad) return pendingLoad

        const loadPromise = Promise.resolve(
            library.sources.map((source) => ({
                note: source.note,
                player: createPlayer(source.url, { preload: false })
            }))
        )
            .then((createdPads) => {
                useStorePads.setState((state) => {
                    const currentPads = state.pads[library.name]
                    if (Array.isArray(currentPads) && currentPads.length === createdPads.length) {
                        return state
                    }

                    return {
                        pads: {
                            ...state.pads,
                            [library.name]: createdPads
                        }
                    }
                })

                return createdPads
            })
            .finally(() => {
                loadingLibrariesRef.current.delete(library.name)
            })

        loadingLibrariesRef.current.set(library.name, loadPromise)
        return loadPromise
    }, [])

    useEffect(() => {
        let cancelled = false

        const currentLibrary = libraries.find((library) => library.name === currentLibraryName) || libraries[0]
        if (!currentLibrary) {
            setSoundsLoaded(false)
            return undefined
        }

        setSoundsLoaded(isLibraryReady(currentLibrary))

        const primeLibraries = async () => {
            await loadAudioLibrary(currentLibrary)
            if (!cancelled) {
                setSoundsLoaded(true)
            }
        }

        void primeLibraries()

        return () => {
            cancelled = true
        }
    }, [libraries, currentLibraryName, isLibraryReady, loadAudioLibrary, setSoundsLoaded])

    useEffect(() => {
        setMasterVolume(volume)
    }, [volume])

    useEffect(() => {
        setHighpassFrequency(highpassFrequency)
    }, [highpassFrequency])

    useEffect(() => {
        setLowpassFrequency(lowpassFrequency)
    }, [lowpassFrequency])

    useEffect(() => {
        const previousPlayback = previousPlaybackRef.current
        const previousPlayer = getPadPlayer(previousPlayback)
        const nextPlayer = getPadPlayer(playback)
        const isSameTrack = previousPlayback?.library === playback?.library
            && previousPlayback?.note === playback?.note
            && previousPlayback?.title === playback?.title

        if (!isSameTrack && previousPlayer && typeof previousPlayer.stop === 'function') {
            previousPlayer.stop()
        }

        if (nextPlayer && typeof nextPlayer.play === 'function') {
            nextPlayer.play()
        }

        previousPlaybackRef.current = playback
    }, [getPadPlayer, playback])

    return { pads }
}

export default useSoundPlayer
